import { requestRecipientDocument, requestRecipientSession } from './recipient-session.js';
export { MAIL_DELIVERY_NOTICE, recipientStatusLabel, signingEventLabel } from './mail-status.js';
export const DEFAULT_SIGNING_PREFERENCES = {
    senderName: '',
    senderEmail: 'rigo@archlabor.co',
    senderBrand: 'arch-labor',
    signingOrder: 'sequential',
    expiresInDays: 0,
    remindersEnabled: false,
    firstReminderAfterDays: 3,
    reminderIntervalDays: 3,
    maxReminders: 2,
    sendCompletionCopies: true,
    sendExpirationNotice: false,
};
let accessTokenProvider;
let accessClient = 'fieldwise-sign';
export function configureSigningAuth(provider, client = 'fieldwise-sign') {
    accessTokenProvider = provider;
    accessClient = client;
}
async function withDeadline(promise, timeoutMs, message) {
    let timer;
    try {
        return await Promise.race([
            promise,
            new Promise((_, reject) => { timer = globalThis.setTimeout(() => reject(new Error(message)), timeoutMs); }),
        ]);
    }
    finally {
        if (timer)
            globalThis.clearTimeout(timer);
    }
}
export class SigningApi {
    baseUrl;
    options;
    recipientBaseUrl;
    constructor(baseUrl = 'http://127.0.0.1:8787', options = {}) {
        this.baseUrl = baseUrl;
        this.options = options;
        this.recipientBaseUrl = baseUrl;
    }
    async createEnvelope(input, pdf, fileName) {
        const body = new FormData();
        body.append('metadata', JSON.stringify(input));
        body.append('document', new Blob([pdf.slice().buffer], { type: 'application/pdf' }), fileName);
        return this.request('/v1/envelopes', { method: 'POST', body }, 120_000);
    }
    sendEnvelope(id) {
        return this.request(`/v1/envelopes/${encodeURIComponent(id)}/send`, { method: 'POST' }, 60_000);
    }
    listEnvelopes() {
        return this.request('/v1/envelopes');
    }
    getPreferences() {
        return this.request('/v1/settings');
    }
    updatePreferences(preferences) {
        return this.request('/v1/settings', {
            method: 'PATCH',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(preferences),
        });
    }
    getEnvelope(id) {
        return this.request(`/v1/envelopes/${encodeURIComponent(id)}`);
    }
    updateEnvelope(id, input) {
        return this.request(`/v1/envelopes/${encodeURIComponent(id)}`, {
            method: 'PATCH',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(input),
        });
    }
    voidEnvelope(id, reason) {
        return this.request(`/v1/envelopes/${encodeURIComponent(id)}/void`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ reason }) });
    }
    organizeEnvelope(id, input) {
        return this.request(`/v1/envelopes/${encodeURIComponent(id)}/organize`, {
            method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input),
        });
    }
    updateAccess(id, accessState) {
        return this.request(`/v1/envelopes/${encodeURIComponent(id)}/access`, {
            method: 'PATCH', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ accessState }),
        });
    }
    listTemplates() {
        return this.request('/v1/templates');
    }
    saveTemplate(input) {
        return this.request('/v1/templates', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(input) });
    }
    deleteTemplate(id) {
        return this.request(`/v1/templates/${encodeURIComponent(id)}`, { method: 'DELETE' });
    }
    addDocumentVersion(id, pdf, input, fileName = 'countersigned.pdf') {
        const body = new FormData();
        body.append('metadata', JSON.stringify(input));
        body.append('document', new Blob([pdf.slice().buffer], { type: 'application/pdf' }), fileName);
        return this.request(`/v1/envelopes/${encodeURIComponent(id)}/versions`, { method: 'POST', body }, 120_000);
    }
    resendInvitation(envelopeId, recipientId) {
        return this.request(`/v1/envelopes/${encodeURIComponent(envelopeId)}/recipients/${encodeURIComponent(recipientId)}/resend`, { method: 'POST' });
    }
    resendCompletedDocument(envelopeId, recipientId) {
        return this.request(`/v1/envelopes/${encodeURIComponent(envelopeId)}/recipients/${encodeURIComponent(recipientId)}/resend-completed`, { method: 'POST' }, 120_000);
    }
    resendCompletedDocumentToMe(envelopeId) {
        return this.request(`/v1/envelopes/${encodeURIComponent(envelopeId)}/resend-completed-to-me`, { method: 'POST' }, 120_000);
    }
    auditEvents(id) {
        return this.request(`/v1/envelopes/${encodeURIComponent(id)}/audit`);
    }
    async download(id, kind) {
        const headers = await this.authorizedHeaders();
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 90_000);
        try {
            const response = await fetch(`${this.baseUrl}/v1/envelopes/${encodeURIComponent(id)}/${kind}`, { headers, signal: controller.signal });
            if (!response.ok) {
                const payload = await response.json().catch(() => ({}));
                throw new Error(payload.error || 'Download failed.');
            }
            return await response.blob();
        }
        catch (reason) {
            if (controller.signal.aborted)
                throw new Error('The signing service took too long to prepare this download. Please try again.');
            throw reason;
        }
        finally {
            clearTimeout(timer);
        }
    }
    async downloadVersion(id, versionId) {
        const headers = await this.authorizedHeaders();
        const response = await fetch(`${this.baseUrl}/v1/envelopes/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}`, { headers });
        if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.error || 'Version download failed.');
        }
        return response.blob();
    }
    async getSignerSession(token, signal) {
        const result = await requestRecipientSession(this.baseUrl, this.options.recipientFallbackUrl, `/v1/sign/${encodeURIComponent(token)}`, { signal });
        this.recipientBaseUrl = result.baseUrl;
        return result.payload;
    }
    sendSignerVerification(token) {
        return this.request(`/v1/sign/${encodeURIComponent(token)}/verification/send`, { method: 'POST' });
    }
    verifySigner(token, code) {
        return this.request(`/v1/sign/${encodeURIComponent(token)}/verification/verify`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ code }) });
    }
    documentUrl(token, verification) {
        return `${this.recipientBaseUrl}/v1/sign/${encodeURIComponent(token)}/document?verification=${encodeURIComponent(verification)}`;
    }
    async getSignerDocument(token, verification, signal) {
        const primary = this.recipientBaseUrl.replace(/\/$/, '');
        const fallback = [this.baseUrl, this.options.recipientFallbackUrl]
            .find((route) => route && route.replace(/\/$/, '') !== primary);
        const result = await requestRecipientDocument(primary, fallback, `/v1/sign/${encodeURIComponent(token)}/document?verification=${encodeURIComponent(verification)}`, { signal });
        this.recipientBaseUrl = result.baseUrl;
        return result.payload;
    }
    complete(token, verification, payload) {
        return this.request(`/v1/sign/${encodeURIComponent(token)}/complete`, {
            method: 'POST', headers: { 'content-type': 'application/json', 'x-signing-verification': verification }, body: JSON.stringify(payload),
        }, 120_000);
    }
    decline(token, verification, reason) {
        return this.request(`/v1/sign/${encodeURIComponent(token)}/decline`, {
            method: 'POST', headers: { 'content-type': 'application/json', 'x-signing-verification': verification }, body: JSON.stringify({ reason }),
        });
    }
    async request(path, init, timeoutMs = 30_000) {
        const headers = new Headers(init?.headers);
        const recipientRequest = path.startsWith('/v1/sign/');
        if (!recipientRequest) {
            const authorized = await this.authorizedHeaders();
            authorized.forEach((value, key) => headers.set(key, value));
        }
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
            const response = await fetch(`${recipientRequest ? this.recipientBaseUrl : this.baseUrl}${path}`, { ...init, headers, signal: controller.signal });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok)
                throw new Error(payload.error || `Signing service returned ${response.status}.`);
            return payload;
        }
        catch (reason) {
            if (controller.signal.aborted)
                throw new Error('The signing service did not respond in time. Check your connection and try again.');
            if (reason instanceof TypeError)
                throw new Error('The signing service could not be reached. Check your connection and try again.');
            throw reason;
        }
        finally {
            clearTimeout(timer);
        }
    }
    async authorizedHeaders() {
        const headers = new Headers();
        const provider = accessTokenProvider?.();
        const token = provider && typeof provider.then === 'function'
            ? await withDeadline(Promise.resolve(provider), 15_000, 'Your sign-in session could not be verified in time. Please sign in again.')
            : provider;
        if (token)
            headers.set('authorization', `Bearer ${token}`);
        if (token && accessClient === 'fieldwise-ops')
            headers.set('x-fieldwise-client', 'fieldwise-ops');
        return headers;
    }
}
